import { useState, useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { type ModalFC } from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { Grid } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { localChatEventsBus, COMPOSER_START_NEW_THREAD } from '../chat.events';

import {
  CATEGORY_ICONS,
  CHAT_PROMPTS,
  CHAT_PROMPTS_MIXED,
} from './chat-prompt-library.constants';
import {
  type CategoryFilterTypes,
  type CategoryTypes,
  type ChatPrompt,
} from './chat-prompt-library.types';

export const ChatPromptLibraryModal: ModalFC<{}> = ({ open, onOpenChange }) => {
  const {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    nonAllCategories,
    filteredPrompts,
  } = useChatPromptManager();

  const handlePromptClick = ({
    prompt,
    title,
  }: {
    prompt: string;
    title: string;
  }) => {
    trackEvent('CHAT_PROMPT_LIBRARY_PROMPT_SELECTED', {
      prompt,
      title,
      origin: 'chat_prompt_library_modal',
    });
    localChatEventsBus.emit(COMPOSER_START_NEW_THREAD, {
      data: {
        message: prompt,
      },
    });
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-w-5xl h-[85vh] bg-background-subtle p-0 flex flex-col overflow-hidden">
        <YStack className="flex-1 min-h-0">
          <YStack className="px-8 pt-8 pb-6 flex-shrink-0">
            <Dialog.Title className="text-2xl text-foreground font-serif">
              Suggestions
            </Dialog.Title>
          </YStack>

          <XStack className="flex-1 min-h-0 items-stretch">
            <YStack className="w-[15rem] flex-shrink-0 px-4 pb-8">
              <YStack className="gap-0.5">
                <CategoryNavItem
                  category="All"
                  isSelected={selectedCategory === 'All'}
                  onClick={() => setSelectedCategory('All')}
                />
              </YStack>
              <YStack className="mt-2 mb-3 mx-2 h-px bg-border-subtle" />
              <YStack className="gap-0.5">
                {nonAllCategories.map((category) => (
                  <CategoryNavItem
                    key={category}
                    category={category}
                    isSelected={selectedCategory === category}
                    onClick={() => setSelectedCategory(category)}
                  />
                ))}
              </YStack>
            </YStack>

            <YStack className="self-start w-px h-[calc(100%-2rem)] bg-border-subtle" />

            <YStack className="flex-1 min-h-0 gap-6 px-8 pb-8">
              <XStack className="relative flex-shrink-0">
                <Icons.search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-foreground-subtle size-4" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full"
                />
              </XStack>

              <YStack className="flex-1 min-h-0">
                <ScrollMask
                  className="h-full [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
                  showEndFade
                >
                  {filteredPrompts.length === 0 ? (
                    <YStack className="items-center justify-center py-12 text-center gap-2">
                      <Icons.search className="size-10 text-foreground-subtle" />
                      <h3 className="text-base font-medium text-foreground">
                        Nothing matches
                      </h3>
                      <p className="text-foreground-subtle">
                        Try a different search or category
                      </p>
                    </YStack>
                  ) : (
                    <Grid className="grid-cols-1 gap-4 pb-8 md:grid-cols-2">
                      {filteredPrompts.map((prompt) => (
                        <PromptCard
                          key={prompt.id}
                          chatPrompt={prompt}
                          onClick={() =>
                            handlePromptClick({
                              prompt: prompt.prompt,
                              title: prompt.question,
                            })
                          }
                        />
                      ))}
                    </Grid>
                  )}
                </ScrollMask>
              </YStack>
            </YStack>
          </XStack>
        </YStack>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const useChatPromptManager = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryFilterTypes>('All');

  const nonAllCategories = Object.keys(CATEGORY_ICONS) as CategoryTypes[];

  const filteredPrompts = useMemo(() => {
    let filtered =
      selectedCategory === 'All'
        ? CHAT_PROMPTS_MIXED
        : CHAT_PROMPTS.filter((prompt) => prompt.category === selectedCategory);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((prompt) =>
        [prompt.question, prompt.category, prompt.teaser].some((field) =>
          field.toLowerCase().includes(query)
        )
      );
    }

    return filtered;
  }, [searchQuery, selectedCategory]);

  return {
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    nonAllCategories,
    filteredPrompts,
  };
};

const PromptCard = ({
  chatPrompt,
  onClick,
}: {
  chatPrompt: ChatPrompt;
  onClick: () => void;
}) => {
  const Icon = Icons[CATEGORY_ICONS[chatPrompt.category]];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left h-full',
        'bg-surface border border-border-strong rounded-2xl',
        'px-6 pt-4 pb-6 transition-colors duration-150',
        'hover:bg-surface-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background-subtle',
        'group'
      )}
    >
      <YStack className="gap-3 items-start h-full">
        <XStack className="items-center gap-1.5">
          <Icon className="size-3.5 text-foreground-muted" />
          <span className="text-[10px] font-medium uppercase tracking-wider text-foreground-muted">
            {chatPrompt.category}
          </span>
        </XStack>
        <YStack className="gap-1.5 items-start">
          <p className="text-foreground text-base leading-snug">
            {chatPrompt.question}
          </p>
          <p className="text-sm text-foreground-subtle leading-snug">
            {chatPrompt.teaser}
          </p>
        </YStack>
      </YStack>
    </button>
  );
};

const CategoryNavItem = ({
  category,
  isSelected,
  onClick,
}: {
  category: CategoryFilterTypes;
  isSelected: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'w-full text-left rounded-md',
        'px-3 py-1.5 transition-colors duration-75',
        'hover:bg-surface-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-background-subtle',
        isSelected && 'bg-surface-active'
      )}
    >
      <span
        className={cn(
          'transition-colors',
          isSelected ? 'text-foreground font-medium' : 'text-foreground-subtle'
        )}
      >
        {category}
      </span>
    </button>
  );
};
